import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Loader2, Lock, ExternalLink, Download, Calendar, 
  Trophy, Target, Zap, Star, Crown, ChevronRight, ChevronLeft,
  Play, CheckCircle, Circle, Sparkles, BookOpen, Archive,
  FileText, Upload, Menu, X, Home, Clock, ArrowRight, Send
} from 'lucide-react';
import ConsultationScheduler from '../components/ConsultationScheduler';
import api from '../services/api';
import { toast } from 'sonner';

const TIER_CONFIG = {
  baseline: { name: 'Baseline', icon: Zap, color: '#60A5FA', badge: 'BASELINE' },
  academy_major: { name: 'Academy Major', icon: Star, color: '#F97316', badge: 'MAJOR' },
  elite_pivot: { name: 'Elite Pivot', icon: Crown, color: '#D4AF37', badge: 'ELITE' }
};

const ZONE_CONFIG = {
  clogging: { label: 'Clogging Zone', color: '#EF4444' },
  coasting: { label: 'Coasting Zone', color: '#F59E0B' },
  elite: { label: 'Elite Zone', color: '#D4AF37' }
};

// Sidebar Navigation Component
const Sidebar = ({ activeSection, onSectionChange, student, isCollapsed, onToggle }) => {
  const tierConfig = TIER_CONFIG[student?.subscription_tier] || TIER_CONFIG.baseline;
  const TierIcon = tierConfig.icon;
  const isElite = student?.subscription_tier === 'elite_pivot';

  const navItems = [
    { id: 'orientation', label: 'Phase 1: Orientation', icon: Sparkles, description: 'The Alignment Phase', highlight: true },
    { id: 'training', label: 'Training Ground', icon: BookOpen, description: 'Main curriculum' },
    { id: 'combine', label: 'The Combine', icon: Target, description: 'IXL Practice', external: 'https://www.ixl.com/signin/prsvracademy' },
    { id: 'reports', label: 'Scouting Reports', icon: FileText, description: 'Weekly feedback' },
    { id: 'vault', label: 'The Vault', icon: Archive, description: 'Resources & PDFs' },
  ];

  return (
    <div className={`fixed left-0 top-16 h-[calc(100vh-64px)] bg-gray-950 border-r border-gray-800 transition-all duration-300 z-40 ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-4 w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white hover:bg-orange-700 transition-colors"
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Profile Section */}
      {!isCollapsed && (
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-black"
              style={{ backgroundColor: `${tierConfig.color}20`, color: tierConfig.color }}
            >
              {student?.name?.charAt(0) || 'S'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold truncate">{student?.name}</div>
              <div 
                className="text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                style={{ color: tierConfig.color }}
              >
                <TierIcon className="w-3 h-3" />
                {tierConfig.badge}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="p-2">
        {navItems.map((item) => (
          item.external ? (
            <a
              key={item.id}
              href={item.external}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all text-gray-400 hover:text-white hover:bg-white/5`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && (
                <div className="flex-1">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-gray-500">{item.description}</div>
                </div>
              )}
              {!isCollapsed && <ExternalLink className="w-4 h-4 text-gray-600" />}
            </a>
          ) : (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all ${
                item.highlight 
                  ? activeSection === item.id 
                    ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/30' 
                    : 'bg-orange-600/20 text-orange-500 border border-orange-600/30 hover:bg-orange-600/30'
                  : activeSection === item.id 
                    ? 'bg-orange-600/20 text-orange-500' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && (
                <div className="flex-1 text-left">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-gray-500">{item.description}</div>
                </div>
              )}
            </button>
          )
        ))}

        {/* Elite-only: Schedule 1-on-1 */}
        {isElite && (
          <button
            onClick={() => onSectionChange('schedule')}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all border-2 border-amber-500/30 ${
              activeSection === 'schedule' 
                ? 'bg-amber-500/20 text-amber-500' 
                : 'text-amber-500 hover:bg-amber-500/10'
            }`}
          >
            <Calendar className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <div className="flex-1 text-left">
                <div className="font-medium">Schedule 1-on-1</div>
                <div className="text-xs text-amber-600">Elite Exclusive</div>
              </div>
            )}
          </button>
        )}
      </nav>
    </div>
  );
};

// Module Card Component for regular weeks
const ModuleCard = ({ week, onSelectLesson }) => {
  const statusColors = {
    completed: 'border-green-600 bg-green-600/10',
    in_progress: 'border-orange-600 bg-orange-600/10',
    available: 'border-gray-600 bg-gray-900',
    locked: 'border-gray-800 bg-gray-900/50 opacity-60'
  };

  const statusBadges = {
    completed: { label: 'COMPLETED', color: '#22C55E' },
    in_progress: { label: 'IN PROGRESS', color: '#F97316' },
    available: { label: 'AVAILABLE', color: '#60A5FA' },
    locked: { label: 'LOCKED', color: '#6B7280' }
  };

  const badge = statusBadges[week.status];

  return (
    <Card className={`border-2 ${statusColors[week.status]} transition-all hover:scale-[1.02]`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <span 
            className="text-xs font-bold uppercase tracking-wider px-2 py-1"
            style={{ backgroundColor: `${badge.color}20`, color: badge.color }}
          >
            {badge.label}
          </span>
          {week.status === 'locked' && <Lock className="w-4 h-4 text-gray-600" />}
        </div>
        <CardTitle className="text-2xl font-black text-white mt-2">
          Week {week.number}: {week.title}
        </CardTitle>
        <p className="text-gray-400 text-sm">{week.description}</p>
      </CardHeader>
      
      <CardContent>
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-500">Progress</span>
            <span className="text-orange-600 font-bold">{week.progress.completed}/{week.progress.total}</span>
          </div>
          <Progress value={week.progress.percentage} className="h-2" />
        </div>

        {/* Day List */}
        <div className="space-y-2">
          {week.days.map((day) => (
            <button
              key={day.id}
              onClick={() => week.is_unlocked && onSelectLesson(day)}
              disabled={!week.is_unlocked}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                day.completed 
                  ? 'border-green-600/30 bg-green-600/10' 
                  : week.is_unlocked 
                    ? 'border-gray-700 bg-white/5 hover:border-orange-600/50 hover:bg-orange-600/5' 
                    : 'border-gray-800 bg-gray-900/30 cursor-not-allowed'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                day.completed ? 'bg-green-600' : 'bg-gray-800'
              }`}>
                {day.completed ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-gray-400 font-bold">{day.day}</span>
                )}
              </div>
              <div className="flex-1 text-left">
                <div className={`font-medium ${day.completed ? 'text-green-500' : 'text-white'}`}>
                  Day {day.day}: {day.title}
                </div>
                {day.has_upload && (
                  <div className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                    <Upload className="w-3 h-3" /> Screenshot Required
                  </div>
                )}
              </div>
              {week.is_unlocked && <ChevronRight className="w-4 h-4 text-gray-600" />}
            </button>
          ))}
        </div>

        {!week.is_unlocked && (
          <div className="mt-4 text-center text-sm text-gray-500">
            <Clock className="w-4 h-4 inline mr-1" />
            Unlocks {new Date(week.unlock_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced Orientation Lesson Player Component
const OrientationLessonPlayer = ({ 
  lesson, 
  onComplete, 
  onBack, 
  onUpload, 
  onTextSubmit,
  textSubmission,
  setTextSubmission 
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [submittingText, setSubmittingText] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(lesson.completed || false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        await onUpload(lesson.id, e.target.result, file.name, file.type);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
      setUploading(false);
    }
  };

  const handleTextSubmitClick = async () => {
    if (!textSubmission.trim()) {
      toast.error('Please enter your notes');
      return;
    }
    setSubmittingText(true);
    await onTextSubmit(lesson.id, textSubmission);
    setSubmittingText(false);
  };

  const handleCheckboxComplete = async () => {
    setCheckboxChecked(true);
    await onComplete(lesson.id, true);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Orientation
      </button>

      {/* Lesson Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-orange-600 text-sm font-bold uppercase tracking-wider mb-2">
          <Sparkles className="w-4 h-4" />
          Phase 1: Orientation • Day {lesson.day}
        </div>
        <h1 className="text-4xl font-black text-white mb-2">{lesson.title}</h1>
        <p className="text-2xl text-orange-500 font-bold">{lesson.headline}</p>
      </div>

      {/* Video Player */}
      <Card className="bg-gray-900 border-gray-800 mb-8 overflow-hidden">
        <div className="aspect-video bg-black">
          <iframe
            src={lesson.video_url}
            title={`Day ${lesson.day}: ${lesson.title}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <CardContent className="py-4">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Play className="w-4 h-4 text-orange-600" />
            Day {lesson.day}: {lesson.title}
          </h3>
        </CardContent>
      </Card>

      {/* Body Text */}
      <Card className="bg-gray-900 border-gray-800 mb-8">
        <CardContent className="py-6">
          <p className="text-gray-300 text-lg leading-relaxed">{lesson.body_text}</p>
        </CardContent>
      </Card>

      {/* The Drill Section */}
      {lesson.the_drill && lesson.the_drill.length > 0 && (
        <Card className="bg-gray-900 border-orange-600/30 mb-8">
          <CardContent className="py-6">
            <h3 className="text-white font-black text-xl mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-600" />
              THE DRILL
            </h3>
            <ol className="space-y-3">
              {lesson.the_drill.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-300">{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Download Section (if available) */}
      {lesson.has_download && (
        <Card className="bg-gray-900 border-blue-600/30 mb-8">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{lesson.download_label || 'Download Resource'}</h3>
                  <p className="text-gray-500 text-sm">PDF Document</p>
                </div>
              </div>
              <a 
                href={lesson.download_url}
                download
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Section (if required) */}
      {lesson.has_upload && (
        <Card className="bg-gray-900 border-orange-600/30 mb-8">
          <CardContent className="py-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-600" />
              {lesson.upload_label || 'Upload Screenshot'}
            </h3>
            
            {lesson.upload_url ? (
              <div className="flex items-center gap-4 p-4 bg-green-600/10 border border-green-600/30 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div className="flex-1">
                  <div className="text-green-500 font-bold">Screenshot Uploaded</div>
                  <a 
                    href={lesson.upload_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    View uploaded file
                  </a>
                </div>
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="border-gray-600"
                >
                  Replace
                </Button>
              </div>
            ) : (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center cursor-pointer hover:border-orange-600/50 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                    <div className="text-gray-400">Click to upload your screenshot</div>
                    <div className="text-gray-600 text-sm mt-1">PNG, JPG up to 5MB</div>
                  </>
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>
      )}

      {/* Text Submission Section (for Film Study) */}
      {lesson.has_text_submission && (
        <Card className="bg-gray-900 border-purple-600/30 mb-8">
          <CardContent className="py-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              {lesson.text_label || 'Submit Your Notes'}
            </h3>
            
            {lesson.text_submission ? (
              <div className="p-4 bg-green-600/10 border border-green-600/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-500 font-bold">Notes Submitted</span>
                </div>
                <div className="text-gray-300 whitespace-pre-wrap bg-gray-800/50 p-4 rounded-lg">
                  {lesson.text_submission}
                </div>
              </div>
            ) : (
              <>
                <Textarea
                  value={textSubmission}
                  onChange={(e) => setTextSubmission(e.target.value)}
                  placeholder={lesson.text_placeholder || 'Enter your notes here...'}
                  className="bg-gray-800 border-gray-700 text-white min-h-[200px] mb-4"
                />
                <Button
                  onClick={handleTextSubmitClick}
                  disabled={submittingText || !textSubmission.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                >
                  {submittingText ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Submit Film Notes
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Checkbox Completion (for Day 3 and Day 5) */}
      {lesson.has_checkbox && (
        <Card className="bg-gray-900 border-green-600/30 mb-8">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <Checkbox
                id="lesson-checkbox"
                checked={checkboxChecked || lesson.completed}
                onCheckedChange={(checked) => {
                  if (checked && !lesson.completed) {
                    handleCheckboxComplete();
                  }
                }}
                disabled={lesson.completed}
                className="mt-1 border-green-600 data-[state=checked]:bg-green-600"
              />
              <label 
                htmlFor="lesson-checkbox" 
                className={`text-lg cursor-pointer ${
                  lesson.completed ? 'text-green-500' : 'text-white'
                }`}
              >
                {lesson.checkbox_label}
              </label>
            </div>
            {lesson.completed && (
              <div className="mt-4 flex items-center gap-2 text-green-500">
                <CheckCircle className="w-5 h-5" />
                <span className="font-bold">Completed!</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Item */}
      <Card className="bg-gradient-to-r from-orange-600/20 to-orange-800/20 border-orange-600/50 mb-8">
        <CardContent className="py-6">
          <h3 className="text-orange-500 font-black text-lg mb-2 flex items-center gap-2">
            <ArrowRight className="w-5 h-5" />
            ACTION ITEM
          </h3>
          <p className="text-white text-lg">{lesson.action_item}</p>
        </CardContent>
      </Card>

      {/* Mark as Done Button (only for non-checkbox lessons) */}
      {!lesson.has_checkbox && (
        <div className="flex justify-between items-center">
          <div>
            {lesson.completed && (
              <span className="text-green-500 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Completed {lesson.completed_at && `on ${new Date(lesson.completed_at).toLocaleDateString()}`}
              </span>
            )}
          </div>
          <Button 
            onClick={() => onComplete(lesson.id, !lesson.completed)}
            className={`px-8 py-4 font-bold ${
              lesson.completed 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-orange-600 hover:bg-orange-700 text-white'
            }`}
          >
            {lesson.completed ? 'Mark as Incomplete' : 'Mark as Done'}
          </Button>
        </div>
      )}
    </div>
  );
};

// Regular Lesson Player Component
const LessonPlayer = ({ lesson, onComplete, onBack, onUpload }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        await onUpload(lesson.id, e.target.result, file.name, file.type);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Week {lesson.week_number}
      </button>

      {/* Lesson Header */}
      <div className="mb-8">
        <div className="text-orange-600 text-sm font-bold uppercase tracking-wider mb-2">
          Week {lesson.week_number}: {lesson.week_title} • Day {lesson.day}
        </div>
        <h1 className="text-4xl font-black text-white mb-4">{lesson.title}</h1>
        <p className="text-xl text-gray-400">{lesson.description}</p>
      </div>

      {/* Video Player */}
      <Card className="bg-gray-900 border-gray-800 mb-8 overflow-hidden">
        <div className="aspect-video bg-black">
          <iframe
            src={lesson.video_url}
            title={lesson.video_title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <CardContent className="py-4">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Play className="w-4 h-4 text-orange-600" />
            {lesson.video_title}
          </h3>
        </CardContent>
      </Card>

      {/* Upload Section (if required) */}
      {lesson.has_upload && (
        <Card className="bg-gray-900 border-orange-600/30 mb-8">
          <CardContent className="py-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-600" />
              {lesson.upload_label || 'Upload Screenshot'}
            </h3>
            
            {lesson.upload_url ? (
              <div className="flex items-center gap-4 p-4 bg-green-600/10 border border-green-600/30 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div className="flex-1">
                  <div className="text-green-500 font-bold">Screenshot Uploaded</div>
                  <a 
                    href={lesson.upload_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    View uploaded file
                  </a>
                </div>
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="border-gray-600"
                >
                  Replace
                </Button>
              </div>
            ) : (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center cursor-pointer hover:border-orange-600/50 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                    <div className="text-gray-400">Click to upload your IXL diagnostic screenshot</div>
                    <div className="text-gray-600 text-sm mt-1">PNG, JPG up to 5MB</div>
                  </>
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>
      )}

      {/* Mark as Done Button */}
      <div className="flex justify-between items-center">
        <div>
          {lesson.completed && (
            <span className="text-green-500 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Completed {lesson.completed_at && `on ${new Date(lesson.completed_at).toLocaleDateString()}`}
            </span>
          )}
        </div>
        <Button 
          onClick={() => onComplete(lesson.id, !lesson.completed)}
          className={`px-8 py-4 font-bold ${
            lesson.completed 
              ? 'bg-gray-700 hover:bg-gray-600 text-white' 
              : 'bg-orange-600 hover:bg-orange-700 text-white'
          }`}
        >
          {lesson.completed ? 'Mark as Incomplete' : 'Mark as Done'}
        </Button>
      </div>
    </div>
  );
};

// Main LMS Dashboard Component
const AcademyLMS = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [vaultResources, setVaultResources] = useState([]);
  const [scoutingReports, setScoutingReports] = useState([]);
  const [activeSection, setActiveSection] = useState('orientation'); // Default to orientation
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [textSubmission, setTextSubmission] = useState('');
  const [isOrientationLesson, setIsOrientationLesson] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) {
      toast.error('Please log in');
      navigate('/academy/enroll');
      return;
    }

    try {
      // Load dashboard data
      const dashboardRes = await api.get('/academy/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (dashboardRes.data.success) {
        setStudent(dashboardRes.data.student);
      }

      // Load curriculum
      const curriculumRes = await api.get('/academy/lms/curriculum', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (curriculumRes.data.success) {
        setOrientation(curriculumRes.data.orientation);
        setCurriculum(curriculumRes.data.curriculum);
        setVaultResources(curriculumRes.data.vault_resources);
      }

      // Load scouting reports
      try {
        const reportsRes = await api.get('/academy/lms/scouting-reports', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (reportsRes.data.success) {
          setScoutingReports(reportsRes.data.reports);
        }
      } catch (e) {
        // Reports might not exist yet
      }

    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Please complete enrollment');
        navigate('/academy/enroll');
      } else if (error.response?.status === 401) {
        localStorage.removeItem('academy_token');
        navigate('/academy/enroll');
      }
    } finally {
      setLoading(false);
    }
  };

  // Open orientation lesson
  const openOrientationLesson = async (day) => {
    const token = localStorage.getItem('academy_token');
    try {
      const response = await api.get(`/academy/lms/lesson/${day.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedLesson(response.data.lesson);
        setIsOrientationLesson(true);
        setTextSubmission(response.data.lesson.text_submission || '');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load lesson');
    }
  };

  // Open regular lesson
  const handleSelectLesson = async (lesson) => {
    const token = localStorage.getItem('academy_token');
    try {
      const response = await api.get(`/academy/lms/lesson/${lesson.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedLesson(response.data.lesson);
        setIsOrientationLesson(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load lesson');
    }
  };

  const handleCompleteLesson = async (lessonId, completed) => {
    const token = localStorage.getItem('academy_token');
    try {
      await api.put('/academy/lms/lesson/progress', 
        { lesson_id: lessonId, completed },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(completed ? 'Lesson completed!' : 'Lesson marked incomplete');
      
      // Refresh curriculum to update progress
      const curriculumRes = await api.get('/academy/lms/curriculum', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (curriculumRes.data.success) {
        setOrientation(curriculumRes.data.orientation);
        setCurriculum(curriculumRes.data.curriculum);
      }

      // Update selected lesson
      setSelectedLesson(prev => ({ ...prev, completed }));
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleUpload = async (lessonId, fileData, fileName, fileType) => {
    const token = localStorage.getItem('academy_token');
    try {
      const response = await api.post('/academy/lms/upload',
        { lesson_id: lessonId, file_data: fileData, file_name: fileName, file_type: fileType },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data.success) {
        toast.success('Screenshot uploaded!');
        setSelectedLesson(prev => ({ ...prev, upload_url: response.data.file_url }));
        
        // Refresh curriculum
        const curriculumRes = await api.get('/academy/lms/curriculum', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (curriculumRes.data.success) {
          setOrientation(curriculumRes.data.orientation);
          setCurriculum(curriculumRes.data.curriculum);
        }
      }
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleTextSubmit = async (lessonId, textContent) => {
    const token = localStorage.getItem('academy_token');
    try {
      await api.post('/academy/lms/lesson/text-submission', {
        lesson_id: lessonId,
        text_content: textContent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Notes submitted!');
      setSelectedLesson(prev => ({ ...prev, text_submission: textContent }));
      
      // Refresh curriculum
      const curriculumRes = await api.get('/academy/lms/curriculum', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (curriculumRes.data.success) {
        setOrientation(curriculumRes.data.orientation);
        setCurriculum(curriculumRes.data.curriculum);
      }
    } catch (error) {
      toast.error('Failed to submit notes');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('academy_token');
    navigate('/academy/enroll');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-600" />
      </div>
    );
  }

  const tierConfig = TIER_CONFIG[student?.subscription_tier] || TIER_CONFIG.baseline;
  const isElite = student?.subscription_tier === 'elite_pivot';

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Sidebar */}
      <Sidebar 
        activeSection={activeSection}
        onSectionChange={(section) => {
          if (section === 'schedule') {
            setShowScheduler(true);
          } else {
            setActiveSection(section);
            setSelectedLesson(null);
            setIsOrientationLesson(false);
          }
        }}
        student={student}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container mx-auto px-6 py-8">
          
          {/* Phase 1: Orientation Section */}
          {activeSection === 'orientation' && !selectedLesson && orientation && (
            <div>
              {/* Orientation Header with Prominent Logo */}
              <div className="mb-8">
                <div className="flex items-center gap-6 mb-4">
                  <img 
                    src="/images/scholar-athlete-logo.png" 
                    alt="Scholar Athlete Academy" 
                    className="h-20 md:h-24 w-auto"
                    style={{ filter: 'invert(37%) sepia(100%) saturate(2500%) hue-rotate(2deg) brightness(105%) contrast(105%)' }}
                  />
                  <div>
                    <h1 className="text-3xl font-black text-white">{orientation.title}</h1>
                    <p className="text-lg text-orange-500 font-bold">{orientation.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-400">Core Theme: <span className="text-white font-medium">{orientation.theme}</span></p>
                <p className="text-gray-500 text-sm mt-1">Pace &amp; Pivot Course • Scholar Athlete Academy</p>
                
                {/* Orientation Progress Bar */}
                <div className="mt-6 p-4 bg-gray-900 border border-orange-600/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-bold">Orientation Progress</span>
                    <span className="text-orange-500 font-bold">{orientation.progress?.completed || 0}/{orientation.progress?.total || 5} Complete</span>
                  </div>
                  <Progress value={orientation.progress?.percentage || 0} className="h-3 bg-gray-800" />
                  {orientation.status === 'completed' && (
                    <div className="mt-3 flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-bold">You&apos;re cleared for the Pace &amp; Pivot course!</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Orientation Days Grid */}
              <div className="grid gap-4">
                {orientation.days?.map((day, idx) => (
                  <Card 
                    key={day.id}
                    className={`bg-gray-900 border-gray-800 hover:border-orange-600/50 transition-all cursor-pointer ${
                      day.completed ? 'border-green-600/30 bg-green-600/5' : ''
                    }`}
                    onClick={() => openOrientationLesson(day)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-6">
                        {/* Day Number */}
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black flex-shrink-0 ${
                          day.completed 
                            ? 'bg-green-600/20 text-green-500 border-2 border-green-600' 
                            : 'bg-orange-600/20 text-orange-500 border-2 border-orange-600'
                        }`}>
                          {day.completed ? <CheckCircle className="w-8 h-8" /> : day.day}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <span className="text-orange-600 text-sm font-bold uppercase tracking-wider">Day {day.day}</span>
                            {day.has_upload && <span className="text-xs px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded">Upload Required</span>}
                            {day.has_text_submission && <span className="text-xs px-2 py-0.5 bg-purple-600/20 text-purple-400 rounded">Notes Required</span>}
                            {day.has_download && <span className="text-xs px-2 py-0.5 bg-green-600/20 text-green-400 rounded">Download Available</span>}
                            {day.has_checkbox && <span className="text-xs px-2 py-0.5 bg-yellow-600/20 text-yellow-400 rounded">Checkpoint</span>}
                          </div>
                          <h3 className="text-xl font-bold text-white mb-1">{day.title}</h3>
                          <p className="text-gray-400 text-sm">{day.headline}</p>
                        </div>
                        
                        {/* Arrow */}
                        <ChevronRight className="w-6 h-6 text-gray-600" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Orientation Lesson Player */}
          {activeSection === 'orientation' && selectedLesson && isOrientationLesson && (
            <OrientationLessonPlayer 
              lesson={selectedLesson}
              onComplete={handleCompleteLesson}
              onBack={() => {
                setSelectedLesson(null);
                setIsOrientationLesson(false);
              }}
              onUpload={handleUpload}
              onTextSubmit={handleTextSubmit}
              textSubmission={textSubmission}
              setTextSubmission={setTextSubmission}
            />
          )}

          {/* Training Ground */}
          {activeSection === 'training' && !selectedLesson && (
            <>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-3xl font-black">Training <span className="text-orange-600">Ground</span></h1>
                  <p className="text-gray-500">Your 30-Day Pace & Pivot Curriculum</p>
                </div>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="border-gray-700 text-gray-400"
                >
                  Log Out
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {curriculum.map((week) => (
                  <ModuleCard 
                    key={week.id}
                    week={week}
                    onSelectLesson={handleSelectLesson}
                  />
                ))}
              </div>
            </>
          )}

          {/* Lesson Player (regular weeks) */}
          {activeSection === 'training' && selectedLesson && !isOrientationLesson && (
            <LessonPlayer 
              lesson={selectedLesson}
              onComplete={handleCompleteLesson}
              onBack={() => setSelectedLesson(null)}
              onUpload={handleUpload}
            />
          )}

          {/* Scouting Reports */}
          {activeSection === 'reports' && (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-black">Scouting <span className="text-orange-600">Reports</span></h1>
                <p className="text-gray-500">Your weekly performance feedback</p>
              </div>

              {scoutingReports.length > 0 ? (
                <div className="space-y-4">
                  {scoutingReports.map((report, idx) => (
                    <Card key={idx} className="bg-gray-900 border-gray-800">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div 
                              className="w-12 h-12 rounded-full flex items-center justify-center text-2xl font-black"
                              style={{ 
                                backgroundColor: report.performance_grade === 'A' ? '#22C55E20' : 
                                                report.performance_grade === 'B' ? '#84CC1620' :
                                                report.performance_grade === 'C' ? '#F59E0B20' : '#EF444420',
                                color: report.performance_grade === 'A' ? '#22C55E' : 
                                      report.performance_grade === 'B' ? '#84CC16' :
                                      report.performance_grade === 'C' ? '#F59E0B' : '#EF4444'
                              }}
                            >
                              {report.performance_grade}
                            </div>
                            <div>
                              <div className="text-white font-bold">Weekly Report</div>
                              <div className="text-gray-500 text-sm">
                                {new Date(report.sent_at).toLocaleDateString('en-US', { 
                                  weekday: 'long',
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">Target Skills</div>
                            <div className="text-orange-600 font-bold">{report.target_skills?.length || 0}</div>
                          </div>
                        </div>
                        {report.scouts_take && (
                          <div className="mt-4 p-4 bg-white/5 rounded-lg">
                            <div className="text-gray-400 text-sm mb-1">Scout&apos;s Take:</div>
                            <div className="text-white">{report.scouts_take}</div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-gray-900 border-gray-800">
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <div className="text-gray-400">No scouting reports yet</div>
                    <div className="text-gray-600 text-sm mt-1">Your first report will arrive after Week 1</div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* The Vault */}
          {activeSection === 'vault' && (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-black">The <span className="text-orange-600">Vault</span></h1>
                <p className="text-gray-500">Downloadable resources and templates</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vaultResources.map((resource) => (
                  <a 
                    key={resource.id}
                    href={resource.url}
                    download
                    className="block"
                  >
                    <Card className="bg-gray-900 border-gray-800 hover:border-orange-600/50 transition-all cursor-pointer group">
                      <CardContent className="py-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-orange-600/20 flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                            <Download className="w-6 h-6 text-orange-600 group-hover:text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-bold group-hover:text-orange-600 transition-colors">
                              {resource.title}
                            </div>
                            <div className="text-gray-500 text-sm uppercase">{resource.type}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Schedule 1-on-1 Modal (Elite Only) */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-black flex items-center gap-3">
              <Calendar className="w-6 h-6 text-amber-500" />
              Schedule Your <span className="text-amber-500">1-on-1 Call</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <ConsultationScheduler 
              athleteName={student?.name}
              readinessZone={student?.readiness_zone}
              onBookingComplete={() => {
                setShowScheduler(false);
                toast.success('Strategy call booked!');
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademyLMS;
